// ─────────────────────────────────────────────────────────────────────────
// IntegrationsPanel.tsx — Settings → Integrations.
//
// Today: one integration (Microsoft Teams). When others land (Slack,
// Google Workspace, etc.) they'll appear here as additional cards.
//
// Layout when configured:
//   ┌─────────────────────────────────────────────────────────┐
//   │ Microsoft Teams         ✓ Connected                     │
//   │ Tenant ID · Organiser email · Last verified             │
//   │ Per-permission status table                             │
//   │ [ Re-verify ] [ Edit configuration ] [ Disconnect ]     │
//   └─────────────────────────────────────────────────────────┘
//
// Not configured: CTA to set up + link to the setup guide.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import {
  MicrosoftConfigResponse, PermissionResult, VerifyResult,
  disconnectConfig, getConfig, saveConfig, verifyConfig,
} from '../../api/microsoftConfig';

interface Props {
  /** Opens the setup-guide page in a new tab. */
  onOpenSetupGuide: () => void;
}

const REQUIRED_PERMS: { key: string; label: string; required: boolean }[] = [
  { key: 'User.Read.All',                    label: 'User.Read.All',                    required: true  },
  { key: 'OnlineMeetings.ReadWrite.All',     label: 'OnlineMeetings.ReadWrite.All',     required: true  },
  { key: 'Calendars.ReadWrite',              label: 'Calendars.ReadWrite',              required: true  },
  { key: 'OnlineMeetingTranscript.Read.All', label: 'OnlineMeetingTranscript.Read.All', required: false },
  { key: 'Chat.Read.All',                    label: 'Chat.Read.All',                    required: false },
];

export function IntegrationsPanel({ onOpenSetupGuide }: Props) {
  const [config,    setConfig]    = useState<MicrosoftConfigResponse | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyOut, setVerifyOut] = useState<VerifyResult | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // Form state
  const [tenantId,        setTenantId]        = useState('');
  const [appClientId,     setAppClientId]     = useState('');
  const [appClientSecret, setAppClientSecret] = useState('');
  const [organiserEmail,  setOrganiserEmail]  = useState('');
  const [secretExpiresAt, setSecretExpiresAt] = useState('');
  const [saving,          setSaving]          = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const cfg = await getConfig();
      setConfig(cfg);
      setShowForm(!cfg.configured);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  const handleSave = async () => {
    // When replacing an existing config, the secret can be left blank to
    // keep the current one (the backend treats empty secret as "keep
    // existing"). For initial setup, all four fields are required.
    const isReplacing = !!config?.configured;
    if (!tenantId || !appClientId || !organiserEmail) {
      setError('Tenant ID, App Client ID, and Organiser email are required.');
      return;
    }
    if (!isReplacing && !appClientSecret) {
      setError('App Client Secret is required for initial setup.');
      return;
    }
    setSaving(true);
    setError(null);
    setVerifyOut(null);
    try {
      const resp = await saveConfig({
        tenantId, appClientId,
        appClientSecret: appClientSecret || '',   // empty = keep existing
        organiserEmail,
        secretExpiresAt: secretExpiresAt || undefined,
      });
      setConfig(resp);
      setVerifyOut(resp.verify);
      setShowForm(false);
      // wipe form state so the secret isn't kept in memory
      setTenantId(''); setAppClientId(''); setAppClientSecret('');
      setOrganiserEmail(''); setSecretExpiresAt('');
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError(null);
    try {
      const result = await verifyConfig();
      setVerifyOut(result);
      await reload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectConfig();
      setConfig({ configured: false });
      setVerifyOut(null);
      setConfirmDisconnect(false);
      setShowForm(true);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--muted)' }}>Loading integration status…</div>;
  }

  const permResult: PermissionResult | null =
    verifyOut?.permissions || config?.permissionsCheckResult || null;

  const isConfigured = !!config?.configured;
  const verifiedAt   = config?.permissionsVerifiedAt;
  const allRequiredOk = REQUIRED_PERMS
    .filter(p => p.required)
    .every(p => permResult?.[p.key] === 'ok');

  return (
    <div style={{ padding: 24, maxWidth: 760 }}>
      {/* Page title */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--fg)' }}>
          Integrations
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>
          External services CIMA connects to. Today: Microsoft Teams for meetings and transcripts.
        </p>
      </div>

      {/* Microsoft Teams card */}
      <div data-tour="integrations.teams" style={{
        background: 'var(--card)',
        border: '1px solid var(--border-md)',
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        {/* Card header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-md)',
          background: 'var(--surface)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 9,
              background: 'var(--primary-mid)',
              color: 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="m22 8-6 4 6 4V8z" />
                <rect x="2" y="6" width="14" height="12" rx="2" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg)' }}>Microsoft Teams</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>
                Schedule meetings · send calendar invites · pull transcripts
              </div>
            </div>
          </div>

          <StatusBadge configured={isConfigured} allOk={allRequiredOk} />
        </div>

        {/* Card body */}
        <div style={{ padding: '18px 20px' }}>
          {!isConfigured && !showForm && (
            <div>
              <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-soft)' }}>
                Microsoft Teams is not yet connected. CIMA needs this to schedule
                CIMT meetings, send calendar invites, and pull transcripts after
                meetings end. <strong style={{ color: 'var(--fg)' }}>Until you connect Teams,
                new incidents cannot be raised in CIMA.</strong>
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowForm(true)} style={primaryBtn}>
                  Set up Microsoft Teams →
                </button>
                <button onClick={onOpenSetupGuide} style={secondaryBtn}>
                  Read the setup guide
                </button>
              </div>
            </div>
          )}

          {isConfigured && !showForm && (
            <ConnectedView
              config={config!}
              permResult={permResult}
              verifyOut={verifyOut}
              verifying={verifying}
              onReverify={handleVerify}
              onEdit={() => setShowForm(true)}
              onDisconnect={() => setConfirmDisconnect(true)}
              onOpenSetupGuide={onOpenSetupGuide}
            />
          )}

          {showForm && (
            <SetupForm
              isReplacing={isConfigured}
              prefillTenantId={isConfigured ? config?.tenantId : ''}
              prefillAppClientId={isConfigured ? config?.appClientId : ''}
              prefillOrganiserEmail={isConfigured ? config?.organiserEmail : ''}
              prefillSecretExpiresAt={isConfigured ? config?.secretExpiresAt?.slice(0,10) : ''}
              tenantId={tenantId} setTenantId={setTenantId}
              appClientId={appClientId} setAppClientId={setAppClientId}
              appClientSecret={appClientSecret} setAppClientSecret={setAppClientSecret}
              organiserEmail={organiserEmail} setOrganiserEmail={setOrganiserEmail}
              secretExpiresAt={secretExpiresAt} setSecretExpiresAt={setSecretExpiresAt}
              saving={saving}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setError(null); }}
              onOpenSetupGuide={onOpenSetupGuide}
            />
          )}

          {error && (
            <div style={{
              marginTop: 14,
              background: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.25)',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13, color: '#991B1B',
            }}>{error}</div>
          )}

          {/* Verify summary (post-save) */}
          {verifyOut && !showForm && (
            <PermissionTable result={verifyOut.permissions} teamsPremium={verifyOut.teamsPremium} />
          )}
          {!verifyOut && isConfigured && permResult && (
            <PermissionTable result={permResult} teamsPremium={false} />
          )}

          {verifiedAt && !showForm && (
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--subtle)' }}>
              Last verified {timeAgo(verifiedAt)}
            </div>
          )}
        </div>
      </div>

      {/* Confirm-disconnect modal */}
      {confirmDisconnect && (
        <ConfirmModal
          onCancel={() => setConfirmDisconnect(false)}
          onConfirm={handleDisconnect}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function StatusBadge({ configured, allOk }: { configured: boolean; allOk: boolean }) {
  if (!configured) {
    return <Badge label="Not connected" bg="rgba(245,158,11,0.14)" fg="#B45309" />;
  }
  if (allOk) {
    return <Badge label="✓ Connected" bg="rgba(74,222,128,0.18)" fg="#15803D" />;
  }
  return <Badge label="⚠ Needs attention" bg="rgba(245,158,11,0.18)" fg="#B45309" />;
}

function Badge({ label, bg, fg }: { label: string; bg: string; fg: string }) {
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
      background: bg, color: fg,
    }}>{label}</span>
  );
}

function ConnectedView({
  config, permResult, verifyOut, verifying,
  onReverify, onEdit, onDisconnect, onOpenSetupGuide,
}: {
  config: MicrosoftConfigResponse;
  permResult: PermissionResult | null;
  verifyOut: VerifyResult | null;
  verifying: boolean;
  onReverify: () => void;
  onEdit: () => void;
  onDisconnect: () => void;
  onOpenSetupGuide: () => void;
}) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 8, fontSize: 13, marginBottom: 18 }}>
        <div style={{ color: 'var(--muted)' }}>Tenant ID</div>
        <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{config.tenantId}</div>
        <div style={{ color: 'var(--muted)' }}>App Client ID</div>
        <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{config.appClientId}</div>
        <div style={{ color: 'var(--muted)' }}>App Secret</div>
        <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: 'var(--muted)' }}>{config.secretPreview}</div>
        <div style={{ color: 'var(--muted)' }}>Organiser email</div>
        <div style={{ fontWeight: 600 }}>{config.organiserEmail}</div>
        {config.secretExpiresAt && (
          <>
            <div style={{ color: 'var(--muted)' }}>Secret expires</div>
            <div>{new Date(config.secretExpiresAt).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
          </>
        )}
      </div>

      {/* Primary action — replacing the credentials is the most likely thing
          an admin comes to this screen to do (rotating secret, changing
          organiser email, swapping Azure app). Make it visually first. */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border-md)',
        borderRadius: 10,
        padding: '14px 16px',
        marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg)', marginBottom: 2 }}>
            Switching to a different email or Azure app?
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
            Replace the configuration to swap the organiser email or rotate the client secret.
            For a brand-new Azure app, follow the <button onClick={onOpenSetupGuide} style={inlineLinkBtn}>setup guide</button> first to grant API permissions.
          </div>
        </div>
        <button
          onClick={onEdit}
          style={{
            ...primaryBtn,
            padding: '10px 16px', fontSize: 13, fontWeight: 700,
          }}
        >
          Replace credentials →
        </button>
      </div>

      {/* Secondary actions row */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={onReverify}
          disabled={verifying}
          data-tour="integrations.verify_button"
          style={{ ...secondaryBtn, opacity: verifying ? 0.6 : 1 }}
        >
          {verifying ? 'Verifying…' : '↻ Re-verify permissions'}
        </button>
        <button onClick={onOpenSetupGuide} style={secondaryBtn}>
          Setup guide
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={onDisconnect}
          data-tour="integrations.disconnect_button"
          style={{ ...secondaryBtn, color: '#B91C1C', borderColor: 'rgba(220,38,38,0.3)' }}
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

function SetupForm(props: {
  isReplacing?: boolean;
  prefillTenantId?: string;
  prefillAppClientId?: string;
  prefillOrganiserEmail?: string;
  prefillSecretExpiresAt?: string;
  tenantId: string; setTenantId: (s: string) => void;
  appClientId: string; setAppClientId: (s: string) => void;
  appClientSecret: string; setAppClientSecret: (s: string) => void;
  organiserEmail: string; setOrganiserEmail: (s: string) => void;
  secretExpiresAt: string; setSecretExpiresAt: (s: string) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  onOpenSetupGuide: () => void;
}) {
  // Apply prefills once on mount
  useEffect(() => {
    if (props.prefillTenantId && !props.tenantId) props.setTenantId(props.prefillTenantId);
    if (props.prefillAppClientId && !props.appClientId) props.setAppClientId(props.prefillAppClientId);
    if (props.prefillOrganiserEmail && !props.organiserEmail) props.setOrganiserEmail(props.prefillOrganiserEmail);
    if (props.prefillSecretExpiresAt && !props.secretExpiresAt) props.setSecretExpiresAt(props.prefillSecretExpiresAt);
  // Intentionally empty deps — prefill should fire once on mount only
  }, []);  // eslint-disable-line

  return (
    <div data-tour="integrations.form">
      {/* Replacing-an-existing-config banner. Two scenarios:
            1. Same Azure app, new organiser email — leave secret blank
            2. New Azure app entirely — re-do the setup guide steps first  */}
      {props.isReplacing ? (
        <div style={{
          background: 'rgba(99,55,138,0.06)',
          border: '1px solid rgba(99,55,138,0.22)',
          borderLeft: '3px solid var(--primary)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 16,
          fontSize: 12.5, lineHeight: 1.6, color: 'var(--fg-soft)',
        }}>
          <div style={{ fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>
            Replacing the current Teams configuration
          </div>
          Two common cases:
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            <li>
              <strong>Just swapping organiser email</strong> — update the email field, leave the secret blank. We'll keep the existing secret.
            </li>
            <li>
              <strong>Switching to a different Azure app entirely</strong> —
              {' '}<button onClick={props.onOpenSetupGuide} style={inlineLinkBtn}>follow the setup guide</button> for the new app first
              (register app → create client secret → grant API permissions + admin consent), then paste all four values below.
            </li>
          </ul>
        </div>
      ) : (
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>
          Need help getting these values? <button onClick={props.onOpenSetupGuide} data-tour="integrations.setup_guide_link" style={inlineLinkBtn}>Open the step-by-step setup guide →</button>
        </p>
      )}

      <Field label="Tenant ID" value={props.tenantId} setValue={props.setTenantId}
             placeholder="e.g. 9baf4404-72ca-477a-ad42-e5d5964770b7" />
      <Field label="App Client ID" value={props.appClientId} setValue={props.setAppClientId}
             placeholder="From Azure → App registrations → Overview" />
      <Field
        label="App Client Secret"
        value={props.appClientSecret} setValue={props.setAppClientSecret}
        placeholder={props.isReplacing ? "Leave blank to keep the current secret" : "Paste the Value (not the Secret ID)"}
        password
        help={props.isReplacing ? "Only fill this in if you're rotating the secret or switching to a different Azure app." : undefined}
      />
      <Field label="Organiser email" value={props.organiserEmail} setValue={props.setOrganiserEmail}
             placeholder="cima-bot@yourcompany.com.au" />
      <Field label="Secret expiry (optional)" value={props.secretExpiresAt} setValue={props.setSecretExpiresAt}
             placeholder="2028-05-19" type="date"
             help="We'll warn you 30 days before this date so you can rotate." />

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button onClick={props.onSave} disabled={props.saving} style={{
          ...primaryBtn,
          opacity: props.saving ? 0.6 : 1,
          cursor:  props.saving ? 'wait' : 'pointer',
        }}>
          {props.saving ? 'Saving & verifying…' : (props.isReplacing ? 'Save changes & verify' : 'Save & verify')}
        </button>
        <button onClick={props.onCancel} disabled={props.saving} style={secondaryBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({
  label, value, setValue, placeholder, password, type, help,
}: {
  label: string; value: string; setValue: (s: string) => void;
  placeholder?: string; password?: boolean; type?: string; help?: string;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{
        display: 'block', fontSize: 11.5, fontWeight: 700, color: 'var(--muted)',
        letterSpacing: 0.4, marginBottom: 5, textTransform: 'uppercase',
      }}>{label}</label>
      <input
        type={password ? 'password' : (type || 'text')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '9px 12px', fontSize: 13,
          background: 'var(--surface)', border: '1px solid var(--border-md)',
          borderRadius: 8, color: 'var(--fg)', fontFamily: password ? 'var(--font-mono, monospace)' : 'inherit',
          outline: 'none',
        }}
      />
      {help && <div style={{ fontSize: 11, color: 'var(--subtle)', marginTop: 4 }}>{help}</div>}
    </div>
  );
}

function PermissionTable({ result, teamsPremium }: { result: PermissionResult; teamsPremium: boolean }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        fontSize: 11, fontWeight: 800, color: 'var(--muted)',
        letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6,
      }}>Microsoft Graph permissions</div>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-md)',
        borderRadius: 8, overflow: 'hidden',
      }}>
        {REQUIRED_PERMS.map((p, i) => (
          <PermRow key={p.key} label={p.label} required={p.required}
                   status={result[p.key]}
                   last={i === REQUIRED_PERMS.length - 1} />
        ))}
        <PermRow label="Teams Premium license (organiser)" required={false}
                 status={teamsPremium ? 'ok' : 'warn:missing'} last />
      </div>
    </div>
  );
}

function PermRow({ label, status, required, last }: {
  label: string; status: PermissionResult[string]; required: boolean; last: boolean;
}) {
  const colour = statusColour(status, required);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '9px 14px',
      borderBottom: last ? 'none' : '1px solid var(--border-md)',
      fontSize: 12.5,
    }}>
      <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>
        {label}
        {!required && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--subtle)' }}>(optional)</span>}
      </div>
      <span style={{
        padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
        background: colour.bg, color: colour.fg,
      }}>{colour.label}</span>
    </div>
  );
}

function statusColour(status: PermissionResult[string] | undefined, required: boolean) {
  if (status === 'ok') return { label: '✓ Granted', bg: 'rgba(74,222,128,0.18)', fg: '#15803D' };
  if (!status)         return { label: 'Not checked', bg: 'var(--surface)', fg: 'var(--muted)' };
  if (status === 'warn:missing') return { label: 'Recommended', bg: 'rgba(245,158,11,0.14)', fg: '#B45309' };
  if (status === 'token_failed') return { label: 'Auth failed', bg: 'rgba(220,38,38,0.14)', fg: '#B91C1C' };
  const isHard = required;
  return {
    label: status === 'forbidden' ? 'Consent missing' : 'Not granted',
    bg:    isHard ? 'rgba(220,38,38,0.14)' : 'rgba(245,158,11,0.14)',
    fg:    isHard ? '#B91C1C' : '#B45309',
  };
}

function ConfirmModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <>
      <div onClick={onCancel} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 1000,
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 1001, width: 'min(440px, calc(100vw - 32px))',
        background: 'var(--card)', border: '1px solid var(--border-md)',
        borderRadius: 14, padding: 22,
        boxShadow: '0 24px 56px rgba(15,23,42,0.28)',
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 800 }}>Disconnect Microsoft Teams?</h3>
        <p style={{ margin: '0 0 18px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-soft)' }}>
          This will revoke CIMA's access to your Teams tenant and{' '}
          <strong style={{ color: 'var(--fg)' }}>block any new incidents from being raised until you reconnect.</strong>
          {' '}Your existing incidents and meetings remain intact.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={secondaryBtn}>Cancel</button>
          <button onClick={onConfirm} style={{
            ...primaryBtn,
            background: '#B91C1C',
          }}>Disconnect</button>
        </div>
      </div>
    </>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

// ── Shared button styles ──
const primaryBtn: React.CSSProperties = {
  background: 'var(--primary)',
  color: 'white', border: 'none', borderRadius: 10,
  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit',
  boxShadow: '0 2px 8px rgba(99,55,138,0.28)',
};
const secondaryBtn: React.CSSProperties = {
  background: 'var(--card)', color: 'var(--fg)',
  border: '1px solid var(--border-md)', borderRadius: 10,
  padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};
const ghostBtn: React.CSSProperties = {
  background: 'transparent', color: 'var(--muted)',
  border: 'none', borderRadius: 10,
  padding: '9px 4px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit',
};
const inlineLinkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0,
  color: 'var(--primary)', cursor: 'pointer', fontWeight: 700,
  fontSize: 12, fontFamily: 'inherit', textDecoration: 'underline',
};
